import React, {useState, useEffect} from "react";
import {useFormik, getIn} from "formik";
import {useObserver} from "mobx-react-lite";
import {toJS} from "mobx";
import Exceljs from "exceljs";
import moment from "moment";
import {
    Row,
    Col,
    Form,
    Input,
    Button,
    Checkbox,
    Card,
    Switch,
    Space,
    DatePicker,
    Upload,
    message,
    Spin
} from "antd";
import {
    PlusOutlined,
    DeleteOutlined,
    ShopOutlined,
    EnvironmentOutlined,
    RedoOutlined,
    UploadOutlined
} from "@ant-design/icons";
import {useFetch} from "../../hooks/useFetch";
import {useTranslation} from "react-i18next";

import {
    apiCredentialsValidation,
    newRequestValidation,
    credentialsPropType,
    fetchCompanyInfoPropType
} from "./newRequestValidation";
import {newRequestFormPropType} from "./newRequestPropTypes";
import {useStore} from "../../hooks/useStore";
import {SingleMarkerMap} from "../_defaultComponents/SingleMarkerMap/SingleMarkerMap";
import {
    ProductFormItem,
    TitleLabel,
    InputNumberField,
    SelectField,
    DateRangePickerContainer
} from "./NewRequestStyles";
import {courierTypes, paymentTypes} from "../../constants/constants";
import {ErrorFocus} from "../_defaultComponents/ErrorFocus/ErrorFocus";

import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {EligibleCourierSelect} from "../_defaultComponents/EligibleCourierSelect/EligibleCourierSelect";

const generateRandomNumber = () => {
    return moment().format("mmssSSS");
};

const initialProduct = {
    productName: undefined,
    ingredients: undefined,
    amount: undefined,
    productDesiAmount: undefined,
    return: false,
    willBeDeleted: false,
    isTrial: false
};

const initialOrder = {
    index: undefined,
    pickupInfoIndex: undefined,
    receiverName: undefined,
    receiverAddress: undefined,
    receiverAddressDetail: undefined,
    receiverPhone: undefined,
    orderNumber: generateRandomNumber(),
    note: undefined,
    orderDesiAmount: undefined,
    barcode: undefined,
    paymentType: undefined,
    price: undefined,
    dontRingTheBell: false,
    receiverLocation: {lat: 40.9741979031, lon: 29.097525633},
    products: [initialProduct],
    barcodeIsRequired: false,
    source: "manuelv1"
};

const initialPickup = {
    index: undefined,
    pickupPointId: undefined,
    name: undefined,
    phone: undefined,
    address: undefined,
    location: {
        longitude: 29.098532667,
        latitude: 40.974085261
    },
    freePickup: true
};

export const NewRequest = ({deliveryId}) => {
    const stores = useStore();
    const {t} = useTranslation(["newRequest", "paymentTypes", "singleMarkerMap", "courierTypes"]);
    const [token, setToken] = useState(undefined);
    const [companyInfo, setCompanyInfo] = useState({});
    const [showCompanySelectionForm, setShowCompanySelectionForm] = useState(false);
    const [pickupIndexes, setPickupIndexes] = useState([]);
    const [isSlot, setIsSlot] = useState(false);
    const [slotStartDateTime, setSlotStartDateTime] = useState();
    const [slotEndDateTime, setSlotEndDateTime] = useState();
    const [deliveryType, setDeliveryType] = useState("delivery");
    const [useCallbackInfo, setUseCallbackInfo] = useState(false);
    const [orderLoading, orderData, setOrderData, setOrderLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.orderStore.getOrder.bind(null, {
            deliveryId
        }),
        showLoading: true,
        condition: !!deliveryId
    });

    const getToken = async (credentials: credentialsPropType = {}) => {
        try {
            const data = await stores.apiRequestStore.getAuthToken({
                apiKey: credentials.apiKey,
                data: {
                    apiSecret: credentials.apiSecret
                }
            });
            return data.token;
        } catch (err) {
            throw err;
        }
    };
    const fetchCompanyInfo = async ({credentials = {}, token}: fetchCompanyInfoPropType) => {
        try {
            const result = await stores.apiRequestStore.getCompanyInfo({
                token,
                apiKey: credentials.apiKey
            });
            const barcodeIsRequired = result.options.barcode.isActive;
            initialOrder.barcodeIsRequired = barcodeIsRequired;
            if (orderData?.data) {
                orderData.data.delivery.orders.forEach((order, index) => {
                    if (values.orders[index]?.barcodeIsRequired) {
                        values.orders[index].barcodeIsRequired = barcodeIsRequired;
                        if (!("barcode" in Object.keys(order))) order.barcode = undefined;
                        return (order.barcodeIsRequired = barcodeIsRequired);
                    } else {
                        order.barcodeIsRequired = false;
                    }
                });
                if (orderData.data.delivery.callback) {
                    setUseCallbackInfo(true);
                }
            }

            setCompanyInfo(result);
        } catch (err) {
            throw err;
        }
    };
    const apiCredentialsFormik = useFormik({
        initialValues: {
            companyId:
                orderData && orderData.data && orderData.data.company
                    ? orderData.data.company._id
                    : "",
            apiKey:
                orderData && orderData.data && orderData.data.company
                    ? orderData.data.company.apiKey
                    : "",
            apiSecret:
                orderData && orderData.data && orderData.data.company
                    ? orderData.data.company.apiSecret
                    : ""
        },
        validationSchema: apiCredentialsValidation,
        enableReinitialize: true,
        onSubmit: async (values: credentialsPropType) => {
            await handleApiCredentialsSubmit(values);
        }
    });

    const handleApiCredentialsSubmit = async (values: credentialsPropType) => {
        try {
            const token = await getToken(values);
            setToken(token);
            await fetchCompanyInfo({credentials: values, token});
        } catch (err) {
            console.error(err);
        }
    };

    const {
        values: acValues,
        errors: acErrors,
        touched: acTouched,
        handleChange: acHandleChange,
        handleSubmit: acHandleSubmit,
        isSubmitting: acIsSubmitting,
        setFieldValue: acSetFieldValue
    } = apiCredentialsFormik;

    const formik = useFormik({
        initialValues: {
            pickupInfos: [initialPickup],
            courierType: undefined,
            deliveryType: "delivery",
            orders: [initialOrder],
            resend: Boolean(deliveryId),
            ...orderData?.data?.delivery,
            forceCourierId: undefined
        },
        validationSchema: newRequestValidation,
        enableReinitialize: true,
        onSubmit: async (values: newRequestFormPropType) => {
            await handleRequestSubmit(values);
        }
    });

    const handleRequestSubmit = async (values: newRequestFormPropType) => {
        const body = {
            pickupInfos: values.pickupInfos,
            courierType: values.courierType,
            deliveryType: values.deliveryType,
            orders: values.orders,
            customParams: isSlot
                ? {
                      processType: "slot",
                      slot: {
                          startDate: slotStartDateTime,
                          endDate: slotEndDateTime
                      }
                  }
                : undefined
        };

        if (useCallbackInfo && Object.values(values.callback).every(v => !!v)) {
            body.callback = values.callback;
        }

        body.pickupInfos = values.pickupInfos.map((info, i) => {
            const {index, pickupPointId, name, phone, address, location, freePickup, _id} = info;
            if (freePickup) {
                return {index, name, phone, address, location};
            } else {
                return {pickupPointId: deliveryId ? pickupPointId || _id : pickupPointId, index};
            }
        });
        if (deliveryId) {
            body.panelUser = {
                _id: stores.meStore.userId,
                fullName: stores.meStore.fullName
            };
            body["forceCourierId"] = values.forceCourierId;
        }
        body.orders = values.orders.map((order, i) => {
            if (deliveryId) {
                let {
                    index,
                    pickupInfoIndex,
                    products,
                    receiverName,
                    receiverAddressDetail,
                    receiverAddress,
                    receiverLocation,
                    receiverPhone,
                    callback,
                    orderNumber,
                    note,
                    paymentType,
                    price,
                    barcode,
                    dontRingTheBell,
                    source
                } = order;
                return {
                    index,
                    pickupInfoIndex,
                    products,
                    receiverName,
                    callback,
                    receiverAddressDetail,
                    receiverAddress,
                    receiverLocation,
                    receiverPhone,
                    orderNumber,
                    note,
                    paymentType,
                    price: price.toString(),
                    barcode,
                    dontRingTheBell,
                    source
                };
            } else return {...order, price: order.price?.toString()};
        });

        await stores.apiRequestStore.createDelivery({
            apiKey: acValues.apiKey,
            token,
            data: body
        });
    };

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        useFormikContext
    } = formik;

    const checkValidate = ({fieldName}) => {
        const touchedStatus = getIn(touched, fieldName);
        const errorStatus = getIn(errors, fieldName);
        return errorStatus && touchedStatus ? "error" : "success";
    };

    const checkHelp = ({fieldName}) => {
        const touchedStatus = getIn(touched, fieldName);
        const errorStatus = getIn(errors, fieldName);
        return errorStatus && touchedStatus ? errorStatus : null;
    };

    const removeFieldElem = ({fieldName, index}) => {
        const arr = [...(getIn(values, fieldName) || [])];
        arr.splice(index, 1);
        setFieldValue(fieldName, arr);
    };

    const removeMultipleFieldElem = ({fieldName, elements}) => {
        const arr = [...(getIn(values, fieldName) || [])];
        elements.reverse().forEach(element => {
            if (element.willBeDeleted) {
                arr.splice(element.index, 1);
            }
        });
        setFieldValue(fieldName, arr);
    };

    const checkPickupIndex = (indexToSet, pickupIndex) => {
        const indexes = values.pickupInfos.map(inf => inf.index);
        const foundIn = indexes.findIndex(dd => dd === indexToSet);
        if (foundIn === -1 || foundIn === pickupIndex) {
            indexes[pickupIndex] = indexToSet;
            setPickupIndexes(indexes);
            return true;
        }
        return false;
    };

    const onChange = (value, dateString) => {
        setSlotStartDateTime(value[0]._d);
        setSlotEndDateTime(value[1]._d);
    };

    const disabledDate = current => {
        // Can not select days before today and today
        return current && current < moment().endOf("day");
    };

    const setExcelAsInput = async (data, pickupInfos) => {
        const workbook = new Exceljs.Workbook();
        await workbook.xlsx.load(data);
        const json = JSON.stringify(workbook.model);
        const rows = JSON.parse(json).worksheets[0].rows.filter(r => r.cells[0].value != undefined);
        rows.shift();

        const createdOrders = rows.map(row => {
            const {cells} = row;
            const [
                orderNumber,
                receiverName,
                receiverPhone,
                receiverAddress,
                receiverAddressDetail,
                receiverLocationString,
                productName,
                amount,
                ingredients,
                index,
                pickupInfoIndex,
                source
            ] = cells;

            if (
                receiverLocationString?.value == null ||
                receiverLocationString?.value == "" ||
                receiverLocationString?.value == undefined ||
                receiverLocationString?.value == 0
            ) {
                receiverLocationString.value = "0,0";
            }
            const location = receiverLocationString.value.replace(/ /g, "").split(",");
            const receiverLocation = {
                lat: parseFloat(location[0]),
                lon: parseFloat(location[1])
            };

            console.log(source.value, "source.value");

            return {
                index: index.value,
                pickupInfoIndex: pickupInfoIndex?.value || 1,
                receiverName: receiverName.value,
                receiverAddress: receiverAddress.value,
                receiverAddressDetail: receiverAddressDetail.value,
                // TODO: refactor
                receiverPhone:
                    receiverPhone.value.toString().length == 10
                        ? `0${receiverPhone.value}`
                        : receiverPhone.value,
                orderNumber: orderNumber.value.toString(),
                receiverLocation,
                products: [
                    {
                        productName: productName.value,
                        ingredients: ingredients.value,
                        amount: amount.value
                    }
                ],
                barcodeIsRequired: false,
                source: "manuelv1"
            };
        });

        const pickupInfosFromExcel = createdOrders.map(o => o.pickupInfoIndex);
        const pickupInfosFromForm = pickupInfos.map(p => p.index);
        const difference = pickupInfosFromExcel
            .filter(x => !pickupInfosFromForm.includes(x))
            .concat(pickupInfosFromForm.filter(x => !pickupInfosFromExcel.includes(x)));

        if (difference.length > 0) {
            message.error(t("newRequest.checkWarehouseNumbers", {ns: "newRequest"}));
            return {result: false};
        }

        setFieldValue("orders", createdOrders);
        return {result: true, length: createdOrders.length};
    };

    useEffect(() => {
        if (values && values.pickupInfos && values.pickupInfos.length > 0) {
            setPickupIndexes(values.pickupInfos.map(inf => inf.index));
        }
    }, [values]);

    useEffect(() => {
        if (orderData?.data?.delivery) {
            setDeliveryType(orderData?.data?.delivery?.deliveryType);
            orderData.data.delivery.pickupInfos.forEach(doc => {
                return (doc.freePickup = false);
            });
            orderData.data.delivery.orders.forEach(o => {
                return (o.price = !o?.paymentType ? "" : o.price);
            });
            if (
                orderData.data.delivery.company.apiKey &&
                orderData.data.delivery.company.apiSecret
            ) {
                acSetFieldValue("apiKey", orderData.data.delivery.company.apiKey);
                acSetFieldValue("apiSecret", orderData.data.delivery.company.apiSecret);
                handleApiCredentialsSubmit({
                    apiKey: orderData.data.delivery.company.apiKey,
                    apiSecret: orderData.data.delivery.company.apiSecret
                });
            }
        }
    }, [orderData]);

    useEffect(() => {
        const companyCredentialList = toJS(stores.appStore.companyListFilter);
        if (companyCredentialList.length == 1) {
            const credentials = companyCredentialList[0];
            acSetFieldValue("apiKey", credentials.apiKey);
            acSetFieldValue("apiSecret", credentials.apiSecret);
            handleApiCredentialsSubmit({
                apiKey: credentials.apiKey,
                apiSecret: credentials.apiSecret
            });
        } else if (companyCredentialList.length > 0) {
            if (deliveryId) {
                setShowCompanySelectionForm(false);
            } else {
                setShowCompanySelectionForm(true);
            }
        }
    }, [stores.appStore.companyListFilter]);

    return useObserver(() => (
        <Spin
            spinning={orderLoading}
            style={{marginTop: 60}}
            tip={t("newRequest.orderDataLoading", {
                ns: "newRequest"
            })}
            size="large"
        >
            <Row type="flex" justify="center" data-testid="newRequestScreen">
                <Col span={20}>
                    <Form
                        style={{display: showCompanySelectionForm ? "block" : "none"}}
                        autoComplete="off"
                        labelCol={{span: 24}}
                        wrapperCol={{span: 24}}
                        scrollToFirstError
                        data-testid="newRequestHeaderForm"
                    >
                        <ErrorFocus formik={formik} />
                        <Row gutter={[48, 10]} justify="space-between">
                            <Col span={8}>
                                <Form.Item label={t("fields.company", {ns: "common"})}>
                                    <SelectField
                                        showSearch
                                        data-testid="companySelect"
                                        value={acValues.companyId}
                                        onChange={async value => {
                                            await acSetFieldValue("companyId", value);
                                            if (value) {
                                                const found = await stores.appStore.companyListFilter.find(
                                                    cm => cm._id === value
                                                );
                                                await acSetFieldValue("apiKey", found.apiKey);
                                                await acSetFieldValue("apiSecret", found.apiSecret);
                                            } else {
                                                await acSetFieldValue("apiKey", "");
                                                await acSetFieldValue("apiSecret", "");
                                            }
                                        }}
                                        filterOption={(input, option) =>
                                            option.children
                                                .toLowerCase()
                                                .includes(input.toLowerCase())
                                        }
                                    >
                                        {stores.appStore.companyListFilter
                                            .filter(company => !company.disabled)
                                            .map(cm => (
                                                <SelectField.Option
                                                    key={cm._id}
                                                    value={cm._id}
                                                    data-testid="companySelect_option"
                                                >
                                                    {cm.companyName}
                                                </SelectField.Option>
                                            ))}
                                    </SelectField>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label={t("fields.apiKey", {ns: "common"})}
                                    validateStatus={
                                        acErrors.apiKey && acTouched.apiKey ? "error" : "success"
                                    }
                                    help={
                                        acErrors.apiKey && acTouched.apiKey ? acErrors.apiKey : null
                                    }
                                >
                                    <Input
                                        name="apiKey"
                                        data-testid="apiKeyInput"
                                        value={acValues.apiKey}
                                        onChange={acHandleChange}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    label={t("fields.apiSecret", {ns: "common"})}
                                    validateStatus={
                                        acErrors.apiSecret && acTouched.apiSecret
                                            ? "error"
                                            : "success"
                                    }
                                    help={
                                        acErrors.apiSecret && acTouched.apiSecret
                                            ? acErrors.apiSecret
                                            : null
                                    }
                                >
                                    <Input
                                        name="apiSecret"
                                        data-testid="apiSecretInput"
                                        value={acValues.apiSecret}
                                        onChange={acHandleChange}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row type="flex" justify="end" gutter={[0, 40]}>
                            <CustomButton
                                disabled={acIsSubmitting}
                                type="continueButton"
                                onClick={acHandleSubmit}
                            >
                                {t("actions.continue", {ns: "common"})}
                            </CustomButton>
                        </Row>
                    </Form>

                    {Object.keys(companyInfo).length > 0 && (
                        <Form labelCol={{span: 24}} wrapperCol={{span: 24}} data-testid="orderForm">
                            {/* TODO: styling and better explanation for slot delivery */}
                            {/* <div style={{marginBottom: "16px"}} data-testid="slotCard">
                            <Checkbox
                                name={"slot"}
                                data-testid="orderWithSlot"
                                checked={isSlot}
                                onChange={e => {
                                    setIsSlot(e.target.checked);
                                }}
                            >
                                {t("other.slotOrder", {ns: "common"})}
                            </Checkbox>
                            {isSlot && (
                                <DateRangePickerContainer>
                                    <DatePicker.RangePicker
                                        format="YYYY-MM-DD HH:mm"
                                        data-testid="datepicker"
                                        onChange={onChange}
                                        disabledDate={disabledDate}
                                        showTime={{
                                            hideDisabledOptions: true,
                                            defaultValue: [
                                                moment("00:00:00", "HH:mm"),
                                                moment("11:59:59", "HH:mm")
                                            ]
                                        }}
                                    />
                                </DateRangePickerContainer>
                            )}
                        </div> */}

                            {!deliveryId ? (
                                <Row>
                                    <Col span={24} data-testid="pickupInfoCardCol">
                                        <Card
                                            data-testid="pickupInfoCard"
                                            title={
                                                <div name="pickupInfos">
                                                    <TitleLabel>
                                                        {t("newRequest.fields.warehouses", {
                                                            ns: "newRequest"
                                                        })}
                                                    </TitleLabel>
                                                    {typeof checkHelp({
                                                        fieldName: "pickupInfos"
                                                    }) === "string" && (
                                                        <span
                                                            style={{
                                                                marginLeft: 20,
                                                                color: "#ff4d4f"
                                                            }}
                                                        >
                                                            {checkHelp({fieldName: "pickupInfos"})}
                                                        </span>
                                                    )}
                                                </div>
                                            }
                                            actions={[
                                                <></>,
                                                <></>,
                                                <CustomButton
                                                    data-testid="addPickupButton"
                                                    onClick={() => {
                                                        setFieldValue("pickupInfos", [
                                                            ...(values.pickupInfos || []),

                                                            initialPickup
                                                        ]);
                                                    }}
                                                    icon={<PlusOutlined />}
                                                >
                                                    {t("newRequest.actions.addWarehouse", {
                                                        ns: "newRequest"
                                                    })}
                                                </CustomButton>
                                            ]}
                                        >
                                            {(values.pickupInfos || []).map(
                                                (pickup, pickupIndex) => {
                                                    const pickupPrefix = `pickupInfos.${pickupIndex}.`;

                                                    const pickupCenter = {
                                                        lat: getIn(
                                                            values,
                                                            pickupPrefix + "location.latitude"
                                                        ),
                                                        lng: getIn(
                                                            values,
                                                            pickupPrefix + "location.longitude"
                                                        )
                                                    };
                                                    return (
                                                        <Card
                                                            key={pickupPrefix}
                                                            title={`${t("fields.warehouse", {
                                                                ns: "common"
                                                            })} ${pickupIndex + 1}`}
                                                            bordered={false}
                                                            extra={
                                                                <CustomButton
                                                                    onClick={() => {
                                                                        removeFieldElem({
                                                                            fieldName:
                                                                                "pickupInfos",
                                                                            index: pickupIndex
                                                                        });
                                                                    }}
                                                                    size="small"
                                                                    type="danger"
                                                                    data-testid="pickupDeleteButton"
                                                                    icon={<DeleteOutlined />}
                                                                />
                                                            }
                                                        >
                                                            <Row>
                                                                <Form.Item>
                                                                    <Checkbox
                                                                        data-testid="getFromRegisteredPointCheckbox"
                                                                        name={
                                                                            pickupPrefix +
                                                                            "freePickup"
                                                                        }
                                                                        checked={
                                                                            !getIn(
                                                                                values,
                                                                                pickupPrefix +
                                                                                    "freePickup"
                                                                            )
                                                                        }
                                                                        onChange={e => {
                                                                            setFieldValue(
                                                                                pickupPrefix +
                                                                                    "freePickup",
                                                                                !e.target.checked
                                                                            );
                                                                        }}
                                                                    >
                                                                        {t(
                                                                            "newRequest.actions.getFromRegisteredPoint",
                                                                            {ns: "newRequest"}
                                                                        )}
                                                                    </Checkbox>
                                                                </Form.Item>
                                                            </Row>
                                                            <Row
                                                                justify="center"
                                                                gutter={48}
                                                                data-testid="getFromRegisteredPointForm"
                                                            >
                                                                {!getIn(
                                                                    values,
                                                                    pickupPrefix + "freePickup"
                                                                ) ? (
                                                                    <Col span={24}>
                                                                        <Row gutter={24}>
                                                                            <Col span={15}>
                                                                                <Form.Item
                                                                                    label={t(
                                                                                        "newRequest.fields.registeredWarehouse",
                                                                                        {
                                                                                            ns:
                                                                                                "newRequest"
                                                                                        }
                                                                                    )}
                                                                                    validateStatus={checkValidate(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "pickupPointId"
                                                                                        }
                                                                                    )}
                                                                                    help={checkHelp(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "pickupPointId"
                                                                                        }
                                                                                    )}
                                                                                >
                                                                                    <SelectField
                                                                                        data-testid="savedPickupSelect"
                                                                                        value={getIn(
                                                                                            values,
                                                                                            pickupPrefix +
                                                                                                "pickupPointId"
                                                                                        )}
                                                                                        name={
                                                                                            pickupPrefix +
                                                                                            "pickupPointId"
                                                                                        }
                                                                                        onChange={value =>
                                                                                            setFieldValue(
                                                                                                pickupPrefix +
                                                                                                    "pickupPointId",
                                                                                                value
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        {(
                                                                                            (companyInfo.workingLocations &&
                                                                                                companyInfo.workingLocations.filter(
                                                                                                    sub =>
                                                                                                        !sub.disabled
                                                                                                )) ||
                                                                                            []
                                                                                        ).map(
                                                                                            subCompany => (
                                                                                                <SelectField.Option
                                                                                                    data-testid="savedPickupSelect_option"
                                                                                                    key={
                                                                                                        subCompany._id
                                                                                                    }
                                                                                                    value={
                                                                                                        subCompany._id
                                                                                                    }
                                                                                                >
                                                                                                    {
                                                                                                        subCompany.name
                                                                                                    }
                                                                                                </SelectField.Option>
                                                                                            )
                                                                                        )}
                                                                                    </SelectField>
                                                                                </Form.Item>
                                                                            </Col>
                                                                            <Col span={9}>
                                                                                <Form.Item
                                                                                    validateStatus={checkValidate(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "index"
                                                                                        }
                                                                                    )}
                                                                                    help={checkHelp(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "index"
                                                                                        }
                                                                                    )}
                                                                                    label={t(
                                                                                        "newRequest.fields.warehousePriorityNumber",
                                                                                        {
                                                                                            ns:
                                                                                                "newRequest"
                                                                                        }
                                                                                    )}
                                                                                >
                                                                                    <InputNumberField
                                                                                        data-testid="pickupPriorityNumberInput"
                                                                                        name={
                                                                                            pickupPrefix +
                                                                                            "index"
                                                                                        }
                                                                                        value={getIn(
                                                                                            values,
                                                                                            pickupPrefix +
                                                                                                "index"
                                                                                        )}
                                                                                        parser={val => {
                                                                                            if (
                                                                                                isNaN(
                                                                                                    parseFloat(
                                                                                                        val
                                                                                                    )
                                                                                                )
                                                                                            )
                                                                                                return;
                                                                                            return parseFloat(
                                                                                                val
                                                                                            );
                                                                                        }}
                                                                                        min={1}
                                                                                        onChange={value => {
                                                                                            const isValidPickupIndex = checkPickupIndex(
                                                                                                value,
                                                                                                pickupIndex
                                                                                            );

                                                                                            if (
                                                                                                isValidPickupIndex
                                                                                            ) {
                                                                                                setFieldValue(
                                                                                                    pickupPrefix +
                                                                                                        "index",
                                                                                                    value
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </Form.Item>
                                                                            </Col>
                                                                        </Row>
                                                                    </Col>
                                                                ) : (
                                                                    <Col span={24}>
                                                                        <Row gutter={24}>
                                                                            <Col span={15}>
                                                                                <Form.Item
                                                                                    label={t(
                                                                                        "fields.warehouseName",
                                                                                        {
                                                                                            ns:
                                                                                                "common"
                                                                                        }
                                                                                    )}
                                                                                    validateStatus={checkValidate(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "name"
                                                                                        }
                                                                                    )}
                                                                                    help={checkHelp(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "name"
                                                                                        }
                                                                                    )}
                                                                                >
                                                                                    <Input
                                                                                        data-testid="pickupNameInput"
                                                                                        name={
                                                                                            pickupPrefix +
                                                                                            "name"
                                                                                        }
                                                                                        value={getIn(
                                                                                            values,
                                                                                            pickupPrefix +
                                                                                                "name"
                                                                                        )}
                                                                                        onChange={
                                                                                            handleChange
                                                                                        }
                                                                                    />
                                                                                </Form.Item>
                                                                            </Col>
                                                                            <Col span={9}>
                                                                                <Form.Item
                                                                                    label={t(
                                                                                        "newRequest.fields.warehousePriorityNumber",
                                                                                        {
                                                                                            ns:
                                                                                                "newRequest"
                                                                                        }
                                                                                    )}
                                                                                    validateStatus={checkValidate(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "index"
                                                                                        }
                                                                                    )}
                                                                                    help={checkHelp(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "index"
                                                                                        }
                                                                                    )}
                                                                                >
                                                                                    <InputNumberField
                                                                                        name={
                                                                                            pickupPrefix +
                                                                                            "index"
                                                                                        }
                                                                                        data-testid="pickupPriorityNumberInput"
                                                                                        parser={val => {
                                                                                            if (
                                                                                                isNaN(
                                                                                                    parseFloat(
                                                                                                        val
                                                                                                    )
                                                                                                )
                                                                                            )
                                                                                                return;
                                                                                            return parseFloat(
                                                                                                val
                                                                                            );
                                                                                        }}
                                                                                        min={1}
                                                                                        value={getIn(
                                                                                            values,
                                                                                            pickupPrefix +
                                                                                                "index"
                                                                                        )}
                                                                                        onChange={value => {
                                                                                            const isValidPickupIndex = checkPickupIndex(
                                                                                                value,
                                                                                                pickupIndex
                                                                                            );

                                                                                            if (
                                                                                                isValidPickupIndex
                                                                                            ) {
                                                                                                setFieldValue(
                                                                                                    pickupPrefix +
                                                                                                        "index",
                                                                                                    value
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </Form.Item>
                                                                            </Col>
                                                                        </Row>

                                                                        <Form.Item
                                                                            validateStatus={checkValidate(
                                                                                {
                                                                                    fieldName:
                                                                                        pickupPrefix +
                                                                                        "phone"
                                                                                }
                                                                            )}
                                                                            help={checkHelp({
                                                                                fieldName:
                                                                                    pickupPrefix +
                                                                                    "phone"
                                                                            })}
                                                                            label={t(
                                                                                "newRequest.fields.warehousePhoneNo",
                                                                                {ns: "newRequest"}
                                                                            )}
                                                                        >
                                                                            <Input
                                                                                name={
                                                                                    pickupPrefix +
                                                                                    "phone"
                                                                                }
                                                                                data-testid="pickupPhoneNumberInput"
                                                                                value={getIn(
                                                                                    values,
                                                                                    pickupPrefix +
                                                                                        "phone"
                                                                                )}
                                                                                onChange={
                                                                                    handleChange
                                                                                }
                                                                            />
                                                                        </Form.Item>
                                                                        <Form.Item
                                                                            validateStatus={checkValidate(
                                                                                {
                                                                                    fieldName:
                                                                                        pickupPrefix +
                                                                                        "address"
                                                                                }
                                                                            )}
                                                                            help={checkHelp({
                                                                                fieldName:
                                                                                    pickupPrefix +
                                                                                    "address"
                                                                            })}
                                                                            label={t(
                                                                                "newRequest.fields.warehouseAddress",
                                                                                {ns: "newRequest"}
                                                                            )}
                                                                        >
                                                                            <Input.TextArea
                                                                                rows={3}
                                                                                name={
                                                                                    pickupPrefix +
                                                                                    "address"
                                                                                }
                                                                                data-testid="pickupAddress"
                                                                                disabled={
                                                                                    !getIn(
                                                                                        values,
                                                                                        pickupPrefix +
                                                                                            "freePickup"
                                                                                    )
                                                                                }
                                                                                value={getIn(
                                                                                    values,
                                                                                    pickupPrefix +
                                                                                        "address"
                                                                                )}
                                                                                onChange={
                                                                                    handleChange
                                                                                }
                                                                            />
                                                                        </Form.Item>

                                                                        <Row
                                                                            gutter={24}
                                                                            data-testid="pickupLocation"
                                                                        >
                                                                            <Col span={24}>
                                                                                <Form.Item
                                                                                    label={t(
                                                                                        "fields.location",
                                                                                        {
                                                                                            ns:
                                                                                                "common"
                                                                                        }
                                                                                    )}
                                                                                    validateStatus={checkValidate(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "location"
                                                                                        }
                                                                                    )}
                                                                                    help={checkHelp(
                                                                                        {
                                                                                            fieldName:
                                                                                                pickupPrefix +
                                                                                                "location"
                                                                                        }
                                                                                    )}
                                                                                >
                                                                                    <Row
                                                                                        type="flex"
                                                                                        gutter={16}
                                                                                        justify="center"
                                                                                    >
                                                                                        <Col data-testid="pickupLat">
                                                                                            {`${t(
                                                                                                "fields.latitude",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )}: ${pickupCenter.lat.toFixed(
                                                                                                5
                                                                                            )}`}
                                                                                        </Col>
                                                                                        <Col data-testid="pickupLng">
                                                                                            {`${t(
                                                                                                "fields.longitude",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )}: ${pickupCenter.lng.toFixed(
                                                                                                5
                                                                                            )}`}
                                                                                        </Col>
                                                                                    </Row>

                                                                                    <SingleMarkerMap
                                                                                        center={
                                                                                            pickupCenter
                                                                                        }
                                                                                        onClick={({
                                                                                            lat,
                                                                                            lng
                                                                                        }) => {
                                                                                            const c = {
                                                                                                longitude: lng,
                                                                                                latitude: lat
                                                                                            };
                                                                                            setFieldValue(
                                                                                                pickupPrefix +
                                                                                                    "location",
                                                                                                c
                                                                                            );
                                                                                        }}
                                                                                        icon={
                                                                                            <ShopOutlined />
                                                                                        }
                                                                                        addExternalInput
                                                                                    />
                                                                                </Form.Item>
                                                                            </Col>
                                                                        </Row>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                        </Card>
                                                    );
                                                }
                                            )}
                                        </Card>
                                    </Col>
                                </Row>
                            ) : (
                                <Row>
                                    <Col span={24} data-testid="pickupInfoCardCol">
                                        <Card
                                            data-testid="pickupInfoCard"
                                            title={
                                                <div name="pickupInfos">
                                                    <TitleLabel>
                                                        {t("newRequest.fields.warehouses", {
                                                            ns: "newRequest"
                                                        })}
                                                    </TitleLabel>
                                                    {typeof checkHelp({
                                                        fieldName: "pickupInfos"
                                                    }) === "string" && (
                                                        <span
                                                            style={{
                                                                marginLeft: 20,
                                                                color: "#ff4d4f"
                                                            }}
                                                        >
                                                            {checkHelp({fieldName: "pickupInfos"})}
                                                        </span>
                                                    )}
                                                </div>
                                            }
                                        >
                                            {(values.pickupInfos || []).map(
                                                (pickup, pickupIndex) => {
                                                    const pickupPrefix = `pickupInfos.${pickupIndex}.`;

                                                    const pickupCenter = {
                                                        lat: getIn(
                                                            values,
                                                            pickupPrefix + "location.latitude"
                                                        ),
                                                        lng: getIn(
                                                            values,
                                                            pickupPrefix + "location.longitude"
                                                        )
                                                    };
                                                    const {
                                                        companyName = "",
                                                        name: pickupName = "",
                                                        address = "",
                                                        city = "",
                                                        location
                                                    } = pickup;
                                                    return (
                                                        <Card
                                                            key={pickupPrefix}
                                                            title={`${t("fields.warehouse", {
                                                                ns: "common"
                                                            })} ${pickupIndex + 1}`}
                                                            bordered={false}
                                                        >
                                                            <Row
                                                                justify="center"
                                                                gutter={48}
                                                                data-testid="getFromRegisteredPointForm"
                                                            >
                                                                <Col span={24}>
                                                                    <Row gutter={24}>
                                                                        <Col span={9}>
                                                                            <Form.Item
                                                                                label={"Firma Adı"}
                                                                            >
                                                                                <Input
                                                                                    disabled
                                                                                    data-testid="pickupNameInput"
                                                                                    value={
                                                                                        companyName
                                                                                    }
                                                                                />
                                                                            </Form.Item>
                                                                        </Col>
                                                                        <Col span={15}>
                                                                            <Form.Item
                                                                                label={t(
                                                                                    "fields.warehouseName",
                                                                                    {
                                                                                        ns: "common"
                                                                                    }
                                                                                )}
                                                                            >
                                                                                <Input
                                                                                    disabled
                                                                                    value={
                                                                                        pickupName
                                                                                    }
                                                                                />
                                                                            </Form.Item>
                                                                        </Col>
                                                                    </Row>

                                                                    <Form.Item
                                                                        label={t(
                                                                            "newRequest.fields.warehouseAddress",
                                                                            {ns: "newRequest"}
                                                                        )}
                                                                    >
                                                                        <Input.TextArea
                                                                            rows={3}
                                                                            disabled
                                                                            value={address}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                        </Card>
                                                    );
                                                }
                                            )}
                                        </Card>
                                    </Col>
                                </Row>
                            )}

                            <Row>
                                <Col span={24} data-testid="courierTypeCard">
                                    <Form.Item
                                        label={t("fields.courierType", {ns: "common"})}
                                        validateStatus={
                                            errors.courierType && touched.courierType
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            errors.courierType && touched.courierType
                                                ? errors.courierType
                                                : null
                                        }
                                    >
                                        <SelectField
                                            id="test"
                                            name="courierType"
                                            data-testid="courierTypeSelect"
                                            value={values.courierType}
                                            onChange={value => setFieldValue("courierType", value)}
                                        >
                                            {courierTypes.map(type => (
                                                <SelectField.Option
                                                    key={type.value}
                                                    value={type.value}
                                                    data-testid="courierTypeSelect_option"
                                                >
                                                    {t(type.translation_key, {ns: "courierTypes"})}
                                                </SelectField.Option>
                                            ))}
                                        </SelectField>
                                    </Form.Item>
                                </Col>
                                <Col span={24} data-testid="deliveryTypeCard">
                                    <Form.Item label={t("fields.deliveryType", {ns: "common"})}>
                                        <SelectField
                                            name="deliveryType"
                                            data-testid="deliveryTypeSelect"
                                            value={deliveryType}
                                            onChange={value => {
                                                setDeliveryType(value);
                                                setFieldValue("deliveryType", value);
                                            }}
                                        >
                                            <SelectField.Option
                                                key={"delivery"}
                                                value={"delivery"}
                                                data-testid="deliveryTypeSelect_option"
                                            >
                                                {t("deliveryTypes.delivery", {ns: "common"})}
                                            </SelectField.Option>
                                            <SelectField.Option
                                                key={"productReturn"}
                                                value={"productReturn"}
                                                data-testid="deliveryTypeSelect_option"
                                            >
                                                {t("deliveryTypes.return", {ns: "common"})}
                                            </SelectField.Option>
                                        </SelectField>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row data-testid="deliveryPointCard">
                                <Col span={24}>
                                    <Card
                                        title={t("newRequest.loadDeliveryPointsFromTemplate", {
                                            ns: "newRequest"
                                        })}
                                        style={{marginBottom: 16}}
                                    >
                                        <Upload
                                            beforeUpload={() => false}
                                            accept=".xlsx,.xls,.csv,.tsv"
                                            onChange={async info => {
                                                if (!info.fileList.length) return false;

                                                if (
                                                    values.pickupInfos
                                                        .map(p => p.index)
                                                        .includes(undefined)
                                                ) {
                                                    message.error(
                                                        t(
                                                            "newRequest.warehousePriorityNumberError",
                                                            {
                                                                ns: "newRequest"
                                                            }
                                                        )
                                                    );
                                                    return false;
                                                }
                                                const action = await setExcelAsInput(
                                                    info.file.originFileObj,
                                                    values.pickupInfos
                                                );
                                                if (!action.result) {
                                                    return false;
                                                } else {
                                                    message.success(
                                                        t(
                                                            "newRequest.loadDeliveryPointsFromTemplateSuccess",
                                                            {
                                                                ns: "newRequest",
                                                                orderNumber: action.length
                                                            }
                                                        )
                                                    );
                                                }
                                            }}
                                        >
                                            <Button
                                                data-testid="uploadButton"
                                                icon={<UploadOutlined />}
                                            >
                                                {t("newRequest.actions.loadFromTemplate", {
                                                    ns: "newRequest"
                                                })}
                                            </Button>
                                        </Upload>
                                    </Card>
                                </Col>
                            </Row>
                            <Row data-testid="ordersCard">
                                <Col span={24}>
                                    <Card
                                        title={
                                            <div name="pickupInfos">
                                                <TitleLabel>
                                                    {t("newRequest.fields.orders", {
                                                        ns: "newRequest"
                                                    })}
                                                </TitleLabel>
                                                {typeof checkHelp({fieldName: "orders"}) ===
                                                    "string" && (
                                                    <span
                                                        style={{marginLeft: 20, color: "#ff4d4f"}}
                                                    >
                                                        {checkHelp({fieldName: "orders"})}
                                                    </span>
                                                )}
                                            </div>
                                        }
                                        actions={[
                                            <></>,
                                            <></>,
                                            <CustomButton
                                                data-testid="addOrderButton"
                                                onClick={() => {
                                                    setFieldValue("orders", [
                                                        ...(values.orders || []),
                                                        {
                                                            ...initialOrder,
                                                            orderNumber: generateRandomNumber()
                                                        }
                                                    ]);
                                                }}
                                                icon={<PlusOutlined />}
                                            >
                                                {t("newRequest.actions.addOrder", {
                                                    ns: "newRequest"
                                                })}
                                            </CustomButton>
                                        ]}
                                    >
                                        {(values.orders || []).map((order, orderIndex) => {
                                            const orderPrefix = `orders.${orderIndex}.`;
                                            const receiverCenter = {
                                                lat: getIn(
                                                    values,
                                                    orderPrefix + "receiverLocation.lat"
                                                ),
                                                lng: getIn(
                                                    values,
                                                    orderPrefix + "receiverLocation.lon"
                                                )
                                            };

                                            return (
                                                <Card
                                                    key={orderPrefix}
                                                    data-testvalue={`order_${orderIndex}`}
                                                    title={`${t("fields.order", {
                                                        ns: "common"
                                                    })} ${orderIndex + 1}`}
                                                    bordered={false}
                                                    extra={
                                                        <CustomButton
                                                            data-testid="deleteORderButton"
                                                            onClick={() => {
                                                                removeFieldElem({
                                                                    fieldName: "orders",
                                                                    index: orderIndex
                                                                });
                                                            }}
                                                            size="small"
                                                            type="danger"
                                                            icon={<DeleteOutlined />}
                                                        />
                                                    }
                                                >
                                                    <Row
                                                        gutter={48}
                                                        data-testid="receiverInformation"
                                                    >
                                                        <Col span={6}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.recipientName",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                validateStatus={checkValidate({
                                                                    fieldName:
                                                                        orderPrefix + "receiverName"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName:
                                                                        orderPrefix + "receiverName"
                                                                })}
                                                            >
                                                                <Input
                                                                    name={
                                                                        orderPrefix + "receiverName"
                                                                    }
                                                                    data-testid="recepientNameInput"
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix + "receiverName"
                                                                    )}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={6}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.recipientPhone",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                validateStatus={checkValidate({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "receiverPhone"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "receiverPhone"
                                                                })}
                                                            >
                                                                <Input
                                                                    name={
                                                                        orderPrefix +
                                                                        "receiverPhone"
                                                                    }
                                                                    data-testid="recepientPhoneInput"
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix +
                                                                            "receiverPhone"
                                                                    )}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                    <Row gutter={48} data-testid="orderInformation">
                                                        <Col span={6}>
                                                            <Form.Item
                                                                label={t("fields.orderNumber", {
                                                                    ns: "common"
                                                                })}
                                                                validateStatus={checkValidate({
                                                                    fieldName:
                                                                        orderPrefix + "orderNumber"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName:
                                                                        orderPrefix + "orderNumber"
                                                                })}
                                                            >
                                                                <Space size={0}>
                                                                    <Input
                                                                        data-testid="orderNumberInput"
                                                                        value={getIn(
                                                                            values,
                                                                            orderPrefix +
                                                                                "orderNumber"
                                                                        )}
                                                                        name={
                                                                            orderPrefix +
                                                                            "orderNumber"
                                                                        }
                                                                        onChange={e => {
                                                                            setFieldValue(
                                                                                orderPrefix +
                                                                                    "orderNumber",
                                                                                e.target.value
                                                                            );
                                                                        }}
                                                                    />
                                                                    <Button
                                                                        type="primary"
                                                                        shape="square"
                                                                        data-testid="orderNumberButton"
                                                                        icon={<RedoOutlined />}
                                                                        onClick={() => {
                                                                            setFieldValue(
                                                                                orderPrefix +
                                                                                    "orderNumber",
                                                                                moment().format(
                                                                                    "mmssSSS"
                                                                                )
                                                                            );
                                                                        }}
                                                                    />
                                                                </Space>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={6}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.orderPriorityNumber",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                validateStatus={checkValidate({
                                                                    fieldName: orderPrefix + "index"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName: orderPrefix + "index"
                                                                })}
                                                            >
                                                                <InputNumberField
                                                                    min={1}
                                                                    name={orderPrefix + "index"}
                                                                    data-testid="orderPriorityNumber"
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix + "index"
                                                                    )}
                                                                    parser={val => {
                                                                        if (isNaN(parseFloat(val)))
                                                                            return;
                                                                        return parseFloat(val);
                                                                    }}
                                                                    onChange={value =>
                                                                        setFieldValue(
                                                                            orderPrefix + "index",
                                                                            value
                                                                        )
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={6}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.orderWarehouseNumber",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                validateStatus={checkValidate({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "pickupInfoIndex"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "pickupInfoIndex"
                                                                })}
                                                            >
                                                                <SelectField
                                                                    data-testid="orderWarehouseNumberSelect"
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix +
                                                                            "pickupInfoIndex"
                                                                    )}
                                                                    onChange={value =>
                                                                        setFieldValue(
                                                                            orderPrefix +
                                                                                "pickupInfoIndex",
                                                                            value
                                                                        )
                                                                    }
                                                                >
                                                                    {pickupIndexes
                                                                        .filter(pi => !!pi)
                                                                        .map(pi => (
                                                                            <SelectField.Option
                                                                                key={pi}
                                                                                value={pi}
                                                                                data-testid="orderWarehouseNumberSelect_option"
                                                                            >
                                                                                {pi}
                                                                            </SelectField.Option>
                                                                        ))}
                                                                </SelectField>
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                    <Row
                                                        gutter={48}
                                                        data-testid="recepientInformation"
                                                    >
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.recipientAddress",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                validateStatus={checkValidate({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "receiverAddress"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "receiverAddress"
                                                                })}
                                                            >
                                                                <Input.TextArea
                                                                    rows={3}
                                                                    data-testid="recepientAddressInput"
                                                                    name={
                                                                        orderPrefix +
                                                                        "receiverAddress"
                                                                    }
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix +
                                                                            "receiverAddress"
                                                                    )}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.recipientAddressDetail",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                validateStatus={checkValidate({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "receiverAddressDetail"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "receiverAddressDetail"
                                                                })}
                                                            >
                                                                <Input.TextArea
                                                                    rows={3}
                                                                    data-testid="recepientAddressDetail"
                                                                    name={
                                                                        orderPrefix +
                                                                        "receiverAddressDetail"
                                                                    }
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix +
                                                                            "receiverAddressDetail"
                                                                    )}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                    <Row gutter={48}>
                                                        <Col span={6}>
                                                            <Form.Item
                                                                label={t("fields.orderNote", {
                                                                    ns: "common"
                                                                })}
                                                            >
                                                                <Input.TextArea
                                                                    rows={3}
                                                                    name={orderPrefix + "note"}
                                                                    data-testid="orderNumberInput"
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix + "note"
                                                                    )}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={6}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.orderDesiAmount",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                validateStatus={checkValidate({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "orderDesiAmount"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName:
                                                                        orderPrefix +
                                                                        "orderDesiAmount"
                                                                })}
                                                            >
                                                                <InputNumberField
                                                                    min={1}
                                                                    name={
                                                                        orderPrefix +
                                                                        "orderDesiAmount"
                                                                    }
                                                                    data-testid="orderPriorityNumber"
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix +
                                                                            "orderDesiAmount"
                                                                    )}
                                                                    parser={val => {
                                                                        if (isNaN(parseFloat(val)))
                                                                            return;
                                                                        return parseFloat(val);
                                                                    }}
                                                                    onChange={value =>
                                                                        setFieldValue(
                                                                            orderPrefix +
                                                                                "orderDesiAmount",
                                                                            value
                                                                        )
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Form.Item hidden>
                                                            <Checkbox
                                                                data-testid="barcodeIsRequired"
                                                                name={`${orderPrefix}barcodeIsRequired`}
                                                                value={
                                                                    companyInfo.options.barcode
                                                                        .isActive
                                                                }
                                                                checked={
                                                                    companyInfo.options.barcode
                                                                        .isActive
                                                                }
                                                            ></Checkbox>
                                                        </Form.Item>
                                                        <Col span={6}>
                                                            <Form.Item
                                                                disabled={
                                                                    !companyInfo.options.barcode
                                                                        .isActive
                                                                }
                                                                label={t("fields.barcode", {
                                                                    ns: "common"
                                                                })}
                                                                validateStatus={checkValidate({
                                                                    fieldName:
                                                                        orderPrefix + "barcode"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName:
                                                                        orderPrefix + "barcode"
                                                                })}
                                                            >
                                                                <Input
                                                                    name={orderPrefix + "barcode"}
                                                                    data-testid="barcode"
                                                                    disabled={
                                                                        !companyInfo.options.barcode
                                                                            .isActive
                                                                    }
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix + "barcode"
                                                                    )}
                                                                    onChange={handleChange}
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                    <Row gutter={48}>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.paymentType",
                                                                    {ns: "newRequest"}
                                                                )}
                                                            >
                                                                <SelectField
                                                                    data-testid="paymentTypeSelect"
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix + "paymentType"
                                                                    )}
                                                                    onChange={value =>
                                                                        setFieldValue(
                                                                            orderPrefix +
                                                                                "paymentType",
                                                                            value
                                                                        )
                                                                    }
                                                                >
                                                                    {paymentTypes.map(pT => (
                                                                        <SelectField.Option
                                                                            key={pT.value}
                                                                            value={pT.value}
                                                                            data-testid="paymentTypeSelect_option"
                                                                        >
                                                                            {t(pT.translation_key, {
                                                                                ns: "paymentTypes"
                                                                            })}
                                                                        </SelectField.Option>
                                                                    ))}
                                                                </SelectField>
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.price",
                                                                    {
                                                                        ns: "newRequest"
                                                                    }
                                                                )}
                                                                validateStatus={checkValidate({
                                                                    fieldName: orderPrefix + "price"
                                                                })}
                                                                help={checkHelp({
                                                                    fieldName: orderPrefix + "price"
                                                                })}
                                                            >
                                                                <InputNumberField
                                                                    min={0}
                                                                    name={orderPrefix + "price"}
                                                                    data-testid="price"
                                                                    value={getIn(
                                                                        values,
                                                                        orderPrefix + "price"
                                                                    )}
                                                                    parser={val => {
                                                                        if (isNaN(parseFloat(val)))
                                                                            return;
                                                                        return parseFloat(val);
                                                                    }}
                                                                    onChange={value =>
                                                                        setFieldValue(
                                                                            orderPrefix + "price",
                                                                            value
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        !getIn(
                                                                            values,
                                                                            orderPrefix +
                                                                                "paymentType"
                                                                        )
                                                                    }
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Form.Item
                                                            label={t(
                                                                "newRequest.fields.dontRingTheBell",
                                                                {ns: "newRequest"}
                                                            )}
                                                        >
                                                            <Switch
                                                                checked={getIn(
                                                                    values,
                                                                    orderPrefix + "dontRingTheBell"
                                                                )}
                                                                checkedChildren={t(
                                                                    "newRequest.actions.dontRingBell",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                unCheckedChildren={t(
                                                                    "newRequest.actions.ringTheBell",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                data-testid="dontRingTheBellSwitch"
                                                                onChange={checked =>
                                                                    setFieldValue(
                                                                        orderPrefix +
                                                                            "dontRingTheBell",
                                                                        checked
                                                                    )
                                                                }
                                                            />
                                                        </Form.Item>
                                                    </Row>
                                                    <Row data-testid="recepientLocation">
                                                        <Col span={24}>
                                                            <Form.Item
                                                                label={t(
                                                                    "newRequest.fields.recipientLocation",
                                                                    {ns: "newRequest"}
                                                                )}
                                                                validateStatus={
                                                                    checkValidate({
                                                                        fieldName:
                                                                            orderPrefix +
                                                                            "receiverLocation.lon"
                                                                    }) ||
                                                                    checkValidate({
                                                                        fieldName:
                                                                            orderPrefix +
                                                                            "receiverLocation.lat"
                                                                    })
                                                                }
                                                                help={
                                                                    checkHelp({
                                                                        fieldName:
                                                                            orderPrefix +
                                                                            "receiverLocation.lon"
                                                                    }) ||
                                                                    checkHelp({
                                                                        fieldName:
                                                                            orderPrefix +
                                                                            "receiverLocation.lat"
                                                                    })
                                                                }
                                                            >
                                                                <Row
                                                                    type="flex"
                                                                    gutter={16}
                                                                    justify="center"
                                                                >
                                                                    <Col data-testid="lat">
                                                                        {`${t("fields.latitude", {
                                                                            ns: "common"
                                                                        })}: ${receiverCenter.lat.toFixed(
                                                                            5
                                                                        )}`}
                                                                    </Col>
                                                                    <Col data-testid="lng">
                                                                        {`${t("fields.longitude", {
                                                                            ns: "common"
                                                                        })}: ${receiverCenter.lng.toFixed(
                                                                            5
                                                                        )}`}
                                                                    </Col>
                                                                </Row>

                                                                <SingleMarkerMap
                                                                    center={receiverCenter}
                                                                    onClick={({lat, lng}) => {
                                                                        const c = {
                                                                            lon: lng,
                                                                            lat: lat
                                                                        };
                                                                        setFieldValue(
                                                                            orderPrefix +
                                                                                "receiverLocation",
                                                                            c
                                                                        );
                                                                    }}
                                                                    icon={<EnvironmentOutlined />}
                                                                    addExternalInput
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>

                                                    <Row data-testid="productsCard">
                                                        <Col span={24}>
                                                            <Card
                                                                size="small"
                                                                bordered
                                                                title={
                                                                    <div
                                                                        name={
                                                                            orderPrefix + "products"
                                                                        }
                                                                    >
                                                                        <TitleLabel>
                                                                            {t(
                                                                                "newRequest.fields.products",
                                                                                {ns: "newRequest"}
                                                                            )}
                                                                        </TitleLabel>
                                                                        {typeof checkHelp({
                                                                            fieldName:
                                                                                orderPrefix +
                                                                                "products"
                                                                        }) === "string" && (
                                                                            <span
                                                                                style={{
                                                                                    marginLeft: 20,
                                                                                    color: "#ff4d4f"
                                                                                }}
                                                                            >
                                                                                {checkHelp({
                                                                                    fieldName:
                                                                                        orderPrefix +
                                                                                        "products"
                                                                                })}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                }
                                                                actions={[
                                                                    <Row
                                                                        justify="end"
                                                                        style={{marginRight: 12}}
                                                                    >
                                                                        <CustomButton
                                                                            type="primary"
                                                                            data-testid="addProduct"
                                                                            onClick={() => {
                                                                                setFieldValue(
                                                                                    orderPrefix +
                                                                                        "products",
                                                                                    [
                                                                                        ...(getIn(
                                                                                            values,
                                                                                            orderPrefix +
                                                                                                "products"
                                                                                        ) || []),
                                                                                        {
                                                                                            ...initialProduct
                                                                                        }
                                                                                    ]
                                                                                );
                                                                            }}
                                                                            icon={<PlusOutlined />}
                                                                        >
                                                                            {t(
                                                                                "newRequest.actions.addProducts",
                                                                                {ns: "newRequest"}
                                                                            )}
                                                                        </CustomButton>
                                                                        {orderPrefix &&
                                                                            (
                                                                                getIn(
                                                                                    values,
                                                                                    orderPrefix +
                                                                                        "products"
                                                                                ) || []
                                                                            ).length > 0 && (
                                                                                <CustomButton
                                                                                    type="primary"
                                                                                    danger
                                                                                    data-testid="deleteSelectedOnes"
                                                                                    onClick={() => {
                                                                                        removeMultipleFieldElem(
                                                                                            {
                                                                                                fieldName:
                                                                                                    orderPrefix +
                                                                                                    "products",
                                                                                                elements: (
                                                                                                    getIn(
                                                                                                        values,
                                                                                                        orderPrefix +
                                                                                                            "products"
                                                                                                    ) ||
                                                                                                    []
                                                                                                ).map(
                                                                                                    (
                                                                                                        product,
                                                                                                        index
                                                                                                    ) => ({
                                                                                                        index,
                                                                                                        willBeDeleted:
                                                                                                            product.willBeDeleted
                                                                                                    })
                                                                                                )
                                                                                            }
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    {t(
                                                                                        "newRequest.actions.deleteSelected",
                                                                                        {
                                                                                            ns:
                                                                                                "newRequest"
                                                                                        }
                                                                                    )}
                                                                                </CustomButton>
                                                                            )}
                                                                    </Row>
                                                                ]}
                                                                headStyle={{fontWeight: "normal"}}
                                                            >
                                                                {(
                                                                    getIn(
                                                                        values,
                                                                        orderPrefix + "products"
                                                                    ) || []
                                                                ).length > 0 && (
                                                                    <Row justify="end">
                                                                        <CustomButton
                                                                            data-testid="selectAllButton"
                                                                            onClick={() => {
                                                                                (
                                                                                    getIn(
                                                                                        values,
                                                                                        orderPrefix +
                                                                                            "products"
                                                                                    ) || []
                                                                                ).map(
                                                                                    (
                                                                                        product,
                                                                                        prodIndex
                                                                                    ) => {
                                                                                        product.willBeDeleted = true;
                                                                                    }
                                                                                );
                                                                                setFieldValue(
                                                                                    orderPrefix +
                                                                                        "products",
                                                                                    [
                                                                                        ...(getIn(
                                                                                            values,
                                                                                            orderPrefix +
                                                                                                "products"
                                                                                        ) || [])
                                                                                    ]
                                                                                );
                                                                            }}
                                                                        >
                                                                            {t(
                                                                                "newRequest.fields.selectAll",
                                                                                {ns: "newRequest"}
                                                                            )}
                                                                        </CustomButton>
                                                                    </Row>
                                                                )}

                                                                {(
                                                                    getIn(
                                                                        values,
                                                                        orderPrefix + "products"
                                                                    ) || []
                                                                ).map((product, prodIndex) => {
                                                                    const productPrefix =
                                                                        orderPrefix +
                                                                        `products.${prodIndex}.`;
                                                                    return (
                                                                        <Card
                                                                            key={productPrefix}
                                                                            data-testid="productRow"
                                                                            data-testvalue={
                                                                                productPrefix
                                                                            }
                                                                            size="small"
                                                                            bordered={false}
                                                                        >
                                                                            <Row gutter={12}>
                                                                                <Col span={3}>
                                                                                    <ProductFormItem
                                                                                        label={
                                                                                            prodIndex ===
                                                                                                0 &&
                                                                                            t(
                                                                                                "newRequest.fields.productReturnCheckbox",
                                                                                                {
                                                                                                    ns:
                                                                                                        "newRequest"
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        validateStatus={checkValidate(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "return"
                                                                                            }
                                                                                        )}
                                                                                        help={checkHelp(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "return"
                                                                                            }
                                                                                        )}
                                                                                    >
                                                                                        <Checkbox
                                                                                            data-testid="returnCheckbox"
                                                                                            name={
                                                                                                productPrefix +
                                                                                                "return"
                                                                                            }
                                                                                            checked={getIn(
                                                                                                values,
                                                                                                productPrefix +
                                                                                                    "return"
                                                                                            )}
                                                                                            onChange={e => {
                                                                                                setFieldValue(
                                                                                                    productPrefix +
                                                                                                        "return",
                                                                                                    e
                                                                                                        .target
                                                                                                        .checked
                                                                                                );
                                                                                            }}
                                                                                        ></Checkbox>
                                                                                    </ProductFormItem>
                                                                                </Col>
                                                                                <Col span={6}>
                                                                                    <ProductFormItem
                                                                                        label={
                                                                                            prodIndex ===
                                                                                                0 &&
                                                                                            t(
                                                                                                "fields.productName",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        validateStatus={checkValidate(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "productName"
                                                                                            }
                                                                                        )}
                                                                                        help={checkHelp(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "productName"
                                                                                            }
                                                                                        )}
                                                                                    >
                                                                                        <Input
                                                                                            data-testid="productNameInput"
                                                                                            name={
                                                                                                productPrefix +
                                                                                                "productName"
                                                                                            }
                                                                                            value={getIn(
                                                                                                values,
                                                                                                productPrefix +
                                                                                                    "productName"
                                                                                            )}
                                                                                            onChange={
                                                                                                handleChange
                                                                                            }
                                                                                        />
                                                                                    </ProductFormItem>
                                                                                </Col>
                                                                                <Col span={3}>
                                                                                    <ProductFormItem
                                                                                        label={
                                                                                            prodIndex ===
                                                                                                0 &&
                                                                                            t(
                                                                                                "fields.productAmount",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        validateStatus={checkValidate(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "amount"
                                                                                            }
                                                                                        )}
                                                                                        help={checkHelp(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "amount"
                                                                                            }
                                                                                        )}
                                                                                    >
                                                                                        <InputNumberField
                                                                                            data-testid="productAmount"
                                                                                            style={{
                                                                                                width:
                                                                                                    "100%"
                                                                                            }}
                                                                                            min={1}
                                                                                            name={
                                                                                                productPrefix +
                                                                                                "amount"
                                                                                            }
                                                                                            parser={val => {
                                                                                                if (
                                                                                                    isNaN(
                                                                                                        parseFloat(
                                                                                                            val
                                                                                                        )
                                                                                                    )
                                                                                                )
                                                                                                    return;
                                                                                                return parseFloat(
                                                                                                    val
                                                                                                );
                                                                                            }}
                                                                                            value={getIn(
                                                                                                values,
                                                                                                productPrefix +
                                                                                                    "amount"
                                                                                            )}
                                                                                            onChange={value =>
                                                                                                setFieldValue(
                                                                                                    productPrefix +
                                                                                                        "amount",
                                                                                                    value
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </ProductFormItem>
                                                                                </Col>
                                                                                <Col span={3}>
                                                                                    <ProductFormItem
                                                                                        label={
                                                                                            prodIndex ===
                                                                                                0 &&
                                                                                            t(
                                                                                                "fields.productDesiAmount",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        validateStatus={checkValidate(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "productDesiAmount"
                                                                                            }
                                                                                        )}
                                                                                        help={checkHelp(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "productDesiAmount"
                                                                                            }
                                                                                        )}
                                                                                    >
                                                                                        <InputNumberField
                                                                                            data-testid="productDesiAmount"
                                                                                            min={1}
                                                                                            name={
                                                                                                productPrefix +
                                                                                                "productDesiAmount"
                                                                                            }
                                                                                            parser={val => {
                                                                                                if (
                                                                                                    isNaN(
                                                                                                        parseFloat(
                                                                                                            val
                                                                                                        )
                                                                                                    )
                                                                                                )
                                                                                                    return;
                                                                                                return parseFloat(
                                                                                                    val
                                                                                                );
                                                                                            }}
                                                                                            value={getIn(
                                                                                                values,
                                                                                                productPrefix +
                                                                                                    "productDesiAmount"
                                                                                            )}
                                                                                            onChange={value =>
                                                                                                setFieldValue(
                                                                                                    productPrefix +
                                                                                                        "productDesiAmount",
                                                                                                    value
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </ProductFormItem>
                                                                                </Col>
                                                                                <Col span={6}>
                                                                                    <ProductFormItem
                                                                                        label={
                                                                                            prodIndex ===
                                                                                                0 &&
                                                                                            t(
                                                                                                "fields.productContent",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        validateStatus={checkValidate(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "ingredients"
                                                                                            }
                                                                                        )}
                                                                                        help={checkHelp(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "ingredients"
                                                                                            }
                                                                                        )}
                                                                                    >
                                                                                        <Input
                                                                                            data-testid="ingredientsInput"
                                                                                            name={
                                                                                                productPrefix +
                                                                                                "ingredients"
                                                                                            }
                                                                                            value={getIn(
                                                                                                values,
                                                                                                productPrefix +
                                                                                                    "ingredients"
                                                                                            )}
                                                                                            onChange={
                                                                                                handleChange
                                                                                            }
                                                                                        />
                                                                                    </ProductFormItem>
                                                                                </Col>
                                                                                <Col span={1}>
                                                                                    <ProductFormItem
                                                                                        label={
                                                                                            prodIndex ===
                                                                                                0 &&
                                                                                            t(
                                                                                                "actions.delete",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        validateStatus={checkValidate(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "willBeDeleted"
                                                                                            }
                                                                                        )}
                                                                                        help={checkHelp(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "willBeDeleted"
                                                                                            }
                                                                                        )}
                                                                                    >
                                                                                        <Checkbox
                                                                                            data-testid="deleteCheckbox"
                                                                                            name={
                                                                                                productPrefix +
                                                                                                "willBeDeleted"
                                                                                            }
                                                                                            checked={getIn(
                                                                                                values,
                                                                                                productPrefix +
                                                                                                    "willBeDeleted"
                                                                                            )}
                                                                                            onChange={e => {
                                                                                                setFieldValue(
                                                                                                    productPrefix +
                                                                                                        "willBeDeleted",
                                                                                                    e
                                                                                                        .target
                                                                                                        .checked
                                                                                                );
                                                                                            }}
                                                                                        ></Checkbox>
                                                                                    </ProductFormItem>
                                                                                </Col>

                                                                                <Col span={2}>
                                                                                    <ProductFormItem
                                                                                        label={
                                                                                            prodIndex ===
                                                                                                0 &&
                                                                                            t(
                                                                                                "actions.trial",
                                                                                                {
                                                                                                    ns:
                                                                                                        "common"
                                                                                                }
                                                                                            )
                                                                                        }
                                                                                        validateStatus={checkValidate(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "isTrial"
                                                                                            }
                                                                                        )}
                                                                                        help={checkHelp(
                                                                                            {
                                                                                                fieldName:
                                                                                                    productPrefix +
                                                                                                    "isTrial"
                                                                                            }
                                                                                        )}
                                                                                    >
                                                                                        <Checkbox
                                                                                            data-testid="deleteCheckbox"
                                                                                            name={
                                                                                                productPrefix +
                                                                                                "isTrial"
                                                                                            }
                                                                                            checked={getIn(
                                                                                                values,
                                                                                                productPrefix +
                                                                                                    "isTrial"
                                                                                            )}
                                                                                            onChange={e => {
                                                                                                setFieldValue(
                                                                                                    productPrefix +
                                                                                                        "isTrial",
                                                                                                    e
                                                                                                        .target
                                                                                                        .checked
                                                                                                );
                                                                                            }}
                                                                                        ></Checkbox>
                                                                                    </ProductFormItem>
                                                                                </Col>
                                                                            </Row>
                                                                        </Card>
                                                                    );
                                                                })}
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Card>
                                            );
                                        })}
                                    </Card>
                                </Col>
                            </Row>

                            <Row style={{marginTop: 16}}>
                                <Col span={24} data-testid="callbackCardCol">
                                    <Card
                                        data-testid="callbackCard"
                                        title={
                                            <div name="callback">
                                                <TitleLabel>Callback</TitleLabel>
                                            </div>
                                        }
                                    >
                                        <Row>
                                            <Form.Item
                                                validateStatus={checkValidate({
                                                    fieldName: "useCallbackInfo"
                                                })}
                                                help={checkHelp({
                                                    fieldName: "useCallbackInfo"
                                                })}
                                            >
                                                <Checkbox
                                                    data-testid="useCallbackInfoCheckbox"
                                                    name="useCallbackInfoCheckbox"
                                                    checked={useCallbackInfo}
                                                    onChange={e => {
                                                        setUseCallbackInfo(e.target.checked);
                                                        values.useCallbackInfo = e.target.checked;
                                                        console.log(values.useCallbackInfo);
                                                    }}
                                                >
                                                    {t("newRequest.fields.useCallbackInfo", {
                                                        ns: "newRequest"
                                                    })}
                                                </Checkbox>
                                            </Form.Item>
                                            <Col span={24}>
                                                <Row gutter={24}>
                                                    <Col span={12}>
                                                        <Form.Item
                                                            label="URL"
                                                            validateStatus={checkValidate({
                                                                fieldName: "callback.url"
                                                            })}
                                                            help={checkHelp({
                                                                fieldName: "callback.url"
                                                            })}
                                                        >
                                                            <Input
                                                                data-testid="callbackURLInput"
                                                                name="callbackURL"
                                                                disabled={!useCallbackInfo}
                                                                value={getIn(
                                                                    values,
                                                                    "callback.url"
                                                                )}
                                                                onChange={e => {
                                                                    setFieldValue(
                                                                        "callback.url",
                                                                        e.target.value
                                                                    );
                                                                }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item
                                                            label="Token"
                                                            validateStatus={checkValidate({
                                                                fieldName: "callback.token"
                                                            })}
                                                            help={checkHelp({
                                                                fieldName: "callback.token"
                                                            })}
                                                        >
                                                            <Input
                                                                data-testid="callbackTokenInput"
                                                                name="callbackToken"
                                                                disabled={!useCallbackInfo}
                                                                value={getIn(
                                                                    values,
                                                                    "callback.token"
                                                                )}
                                                                onChange={e => {
                                                                    setFieldValue(
                                                                        "callback.token",
                                                                        e.target.value
                                                                    );
                                                                }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={6}>
                                                        <Form.Item
                                                            label="Token Header"
                                                            validateStatus={checkValidate({
                                                                fieldName: "callback.tokenHeader"
                                                            })}
                                                            help={checkHelp({
                                                                fieldName: "callback.tokenHeader"
                                                            })}
                                                        >
                                                            <Input
                                                                data-testid="callbackTokenHeaderInput"
                                                                name="callbackTokenHeader"
                                                                disabled={!useCallbackInfo}
                                                                value={getIn(
                                                                    values,
                                                                    "callback.tokenHeader"
                                                                )}
                                                                onChange={e => {
                                                                    setFieldValue(
                                                                        "callback.tokenHeader",
                                                                        e.target.value
                                                                    );
                                                                }}
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Col>
                            </Row>
                            {Boolean(deliveryId) ? (
                                <Row style={{marginTop: 16}}>
                                    <Col span={24} data-testid="forceCourierCardCol">
                                        <Card
                                            data-testid="forceCourierCard"
                                            title={
                                                <div name="callback">
                                                    <TitleLabel>Zorunlu Kurye</TitleLabel>
                                                </div>
                                            }
                                        >
                                            <Row>
                                                <EligibleCourierSelect
                                                    subCompanyId={values?.pickupInfos?.[0]?._id?.toString()}
                                                    courierType={values.courierType}
                                                    initialCall={true}
                                                    value={values.forceCourierId}
                                                    onChange={courierId =>
                                                        setFieldValue("forceCourierId", courierId)
                                                    }
                                                />
                                            </Row>
                                        </Card>
                                    </Col>
                                </Row>
                            ) : (
                                <></>
                            )}
                            <Row type="flex" justify="end" style={{marginTop: 40}}>
                                <CustomButton
                                    type="primary"
                                    data-testid="submitButton"
                                    onClick={handleSubmit}
                                    disabled={
                                        isSubmitting || !stores.meStore.checkRole("newRequest")
                                    }
                                >
                                    {t("actions.save", {ns: "common"})}
                                </CustomButton>
                            </Row>
                        </Form>
                    )}
                </Col>
            </Row>
        </Spin>
    ));
};
